import Clock from "@/assets/icons/clock.svg";
import MarkerPin from "@/assets/icons/marker-pin.svg";
import { ResponseStatus } from "@/domain/entities/agenda/AgendaItemResponseStatus";
import EventDetail, {
    MeetingMessageType,
} from "@/domain/entities/emails/EventDetail";
import useGetStringifiedDateAndHour from "@/hooks/utils/useGetStringifiedDate";
import { cn, isEventPast } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import EventInvitation from "../dashboard/calendar/eventModal/content/EventInvitation";
import EventDateTime from "./EventDateTime";
import EventLocation from "./EventLocation";

export enum EventCardVariant {
    MULTIPLE,
    SINGLE,
}

interface EventCardMeetingsProps {
    event: EventDetail;
    originalEvent?: EventDetail;
    modifierName?: string;
    variant?: EventCardVariant;
    compact?: boolean;
}

const EventCardMeetings = ({
    event,
    originalEvent,
    modifierName,
    variant = EventCardVariant.MULTIPLE,
    compact = false,
}: EventCardMeetingsProps) => {
    const { t } = useTranslation();
    const getStringifiedDate = useGetStringifiedDateAndHour();
    const [updatedEvent, setUpdatedEvent] = useState<EventDetail>(event);

    const hasTimeChanged =
        originalEvent &&
        (originalEvent.startDateTime?.getTime() !==
            event.startDateTime?.getTime() ||
            originalEvent.endDateTime?.getTime() !==
                event.endDateTime?.getTime());

    const hasLocationChanged =
        originalEvent &&
        originalEvent.location?.displayName !== event.location?.displayName;

    return (
        <Card
            className={cn(
                variant === EventCardVariant.SINGLE &&
                    (compact ? "p-2" : "p-4"),
            )}
            size="event"
            variant="event"
        >
            <CardHeader
                className={cn(
                    "mb-0 break-words font-body text-sm",
                    variant === EventCardVariant.SINGLE
                        ? "text-lg font-bold"
                        : "",
                )}
            >
                {updatedEvent.subject}
            </CardHeader>
            {modifierName && (
                <div className="pb-2 text-xs italic opacity-80">
                    {t("widgets.meetings.eventModified", {
                        name: modifierName,
                    })}
                </div>
            )}
            <CardContent
                className={
                    originalEvent && (hasTimeChanged || hasLocationChanged)
                        ? "flex flex-col gap-2"
                        : ""
                }
                variant="event"
            >
                {hasTimeChanged && originalEvent ? (
                    <div className="flex items-start gap-2">
                        <img
                            alt="Clock"
                            className="h-4 w-4 shrink-0"
                            src={Clock}
                        />
                        <div className="flex flex-wrap items-center gap-1">
                            {originalEvent.startDateTime &&
                                originalEvent.endDateTime && (
                                    <span className="text-xs line-through">
                                        {getStringifiedDate(
                                            originalEvent.startDateTime,
                                            originalEvent.endDateTime,
                                        )}
                                    </span>
                                )}
                            <span className="text-xs">→</span>
                            {updatedEvent.startDateTime &&
                                updatedEvent.endDateTime && (
                                    <span className="font-bold">
                                        {getStringifiedDate(
                                            updatedEvent.startDateTime,
                                            updatedEvent.endDateTime,
                                        )}
                                    </span>
                                )}
                        </div>
                    </div>
                ) : (
                    updatedEvent.startDateTime &&
                    updatedEvent.endDateTime && (
                        <EventDateTime
                            endDateTime={updatedEvent.endDateTime}
                            startDateTime={updatedEvent.startDateTime}
                        />
                    )
                )}
                {hasLocationChanged && originalEvent && (
                    <div className="flex items-start gap-2">
                        <img
                            alt="Location"
                            className="h-4 w-4 shrink-0"
                            src={MarkerPin}
                        />
                        <div className="flex flex-wrap items-center gap-1">
                            {originalEvent.location?.displayName && (
                                <span className="text-xs text-white line-through">
                                    {originalEvent.location.displayName}
                                </span>
                            )}
                            <span className="text-xs">→</span>
                            {updatedEvent.location?.displayName && (
                                <span className="break-words text-xs">
                                    {updatedEvent.location.displayName}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>
            <CardFooter className="text-xs">
                {!hasLocationChanged &&
                    !hasTimeChanged &&
                    updatedEvent.location?.displayName && (
                        <EventLocation
                            displayName={updatedEvent.location?.displayName}
                        />
                    )}
            </CardFooter>
            {updatedEvent.userResponseStatus !== ResponseStatus.Organizer &&
                updatedEvent.meetingMessageType !==
                    MeetingMessageType.MEETING_CANCELLED &&
                updatedEvent.userResponseStatus !== ResponseStatus.Declined &&
                !isEventPast(updatedEvent) && (
                    <EventInvitation
                        compact={compact}
                        isSingleEvent={variant === EventCardVariant.SINGLE}
                        setUpdatedEvent={(eventUpdated) =>
                            setUpdatedEvent(eventUpdated as EventDetail)
                        }
                        updatedEvent={updatedEvent}
                    />
                )}
        </Card>
    );
};

export default EventCardMeetings;
